import React, { useState, useMemo } from "react";
import { useDateFilteredLeads } from "@/hooks/useDateFilteredData";
import { useUsers } from "@/hooks";
import { projectService } from "../services/projectService";
import { ProjectsStats } from "../components/ProjectsStats";
import { useDerivedProjectStats } from "@/hooks/useGlobalStats";
import { ProjectsTabContent } from "../components/ProjectsTabContentScreen";
import { AddProject } from "../components/AddProject";
import { ViewProject } from "../components/ViewProject";
import { ChangeStatusForm } from "../components/ChangeStatusForm";
import { ProjectsControlPanel } from "../components/projects_control_panel";
import { AddDesign } from "../verve-design/components/AddDesign";
import { designService } from "../verve-design/services/designService";
import {
  InlineLoader as Loader,
  Pagination,
  ConfirmationModal,
} from "@/components/ui";
import { LEAD_STAGES, PROJECT_PIPELINE_STAGES } from "@/constants/globalConstants";
import { PipelineTabs } from "../../verve-leads/leads/components/PipelineTabs";
import { useAuthStore } from "@/store/authStore";
import {
  showSuccessMessage,
  showApiError,
  showErrorMessage,
} from "@/lib/toast";
import { exportToExcel } from "@/utils/exportUtils";
import { getProjectExportData } from "@/utils/ProjectExport";
import { useProjectFilters } from "../hooks/useProjectFilters";
import { useProjectActivityStats } from "../hooks/useProjectActivityStats";

export default function ProjectsScreen({ activeService = "Verve" }) {
  const { user } = useAuthStore();

  // 1. Filter Management
  const {
    search,
    setSearch,
    debouncedSearch,
    view,
    setView,
    page,
    setPage,
    sortBy,
    setSortBy,
    filters,
    setFilters,
    showAdvancedFilters,
    setShowAdvancedFilters,
  } = useProjectFilters();

  const uRole = (user?.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
  const isProjectManager = ["project-manager", "pm"].includes(uRole);
  const isFieldOfficer = ["field-officer", "fo"].includes(uRole);
  const isAccountant = ["accountant", "account"].includes(uRole);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch,
      page,
      limit: 10,
      status: user?.role === 'designer' 
        ? (filters.projectStatus && filters.projectStatus !== "all" ? filters.projectStatus : undefined)
        : (filters.status !== "all" ? filters.status : (filters.projectStatus && filters.projectStatus !== "all" ? filters.projectStatus : undefined)),
      isMovedToProject: "true",
      hasDesignMedia: user?.role === 'designer' ? "true" : undefined,
      activityStatus:
        filters.activityStatus !== "all" ? filters.activityStatus : undefined,
      source: filters.source !== "all" ? filters.source : undefined,
      city: filters.location !== "all" ? filters.location : undefined,
      assignedTo: filters.assignedTo !== "all" ? filters.assignedTo : undefined,
      date: filters.date !== "all" ? filters.date : undefined,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      sort: sortBy,
    }),
    [debouncedSearch, page, filters, sortBy],
  );

  const activityParams = useMemo(
    () => ({
      search: debouncedSearch,
      status: filters.status !== "all" ? filters.status : (filters.projectStatus && filters.projectStatus !== "all" ? filters.projectStatus : undefined),
      isMovedToProject: "true",
      activityStatus:
        filters.activityStatus !== "all" ? filters.activityStatus : undefined,
      source: filters.source !== "all" ? filters.source : undefined,
      location: filters.location !== "all" ? filters.location : undefined,
      assignedTo: filters.assignedTo !== "all" ? filters.assignedTo : undefined,
      date: filters.date,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    }),
    [debouncedSearch, filters],
  );

  const {
    data: projectsData,
    isLoading: projectsLoading,
    refetch,
  } = useDateFilteredLeads(queryParams, filters);
  const { data: activityStats } = useProjectActivityStats(activityParams);
  const { teamMembers: teamData } = useUsers({ limit: 1000 });

  const rawProjects = Array.isArray(projectsData)
    ? projectsData
    : projectsData?.leads || [];
  const projects = rawProjects.map((l) => ({
    ...l,
    isLocked: l.projectStatus === "project_started",
  }));
  const totalProjects = Array.isArray(projectsData)
    ? projectsData.length
    : projectsData?.total || 0;

  const filterOptions = useMemo(() => {
    if (!rawProjects.length)
      return { locations: [], sources: [], assignees: [] };
    const locations = [
      ...new Set(rawProjects.map((l) => l.city).filter(Boolean)),
    ].sort();
    const sources = [
      ...new Set(rawProjects.map((l) => l.source).filter(Boolean)),
    ].sort();
    const assigneesMap = new Map();
    rawProjects.forEach((l) => {
      const assigned = l.assignedTo?._id
        ? l.assignedTo
        : typeof l.assignedTo === "string"
          ? { _id: l.assignedTo, name: "User" }
          : null;
      if (assigned?._id) assigneesMap.set(assigned._id, assigned.name);
    });
    return {
      locations,
      sources,
      assignees: Array.from(assigneesMap, ([id, name]) => ({ id, name })),
    };
  }, [rawProjects]);

  // Derived statistics using global hook
  const stats = useDerivedProjectStats(projects);

  const team = teamData || [];

  // Filter team members by role and location for assignment
  const salesExecs = useMemo(() => {
    return team.filter((u) => {
      const uRole = (u.role || "")
        .toLowerCase()
        .trim()
        .replace(/[-_\s]+/g, "-");
      return ["sales-exec", "sale-exec", "sales", "sales-executive"].includes(
        uRole,
      );
    });
  }, [team]);

  const projectManagers = useMemo(() => {
    return team.filter((u) => {
      const uRole = (u.role || "")
        .toLowerCase()
        .trim()
        .replace(/[-_\s]+/g, "-");
      return ["project-manager", "pm"].includes(uRole);
    });
  }, [team]);

  // 2. State for Modals
  const [selectedProject, setSelectedProject] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDesignFormOpen, setIsDesignFormOpen] = useState(false);
  const [designModalProject, setDesignModalProject] = useState(null);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    loading: false,
  });
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const activeProject = useMemo(() => {
    if (!selectedProject) return null;
    return (
      projects.find((l) => l._id === selectedProject._id) || selectedProject
    );
  }, [selectedProject, projects]);

  // 3. Handlers
  const handleProjectSubmit = async (data) => {
    try {
      const payload = { ...data };
      if (payload.phone) {
        payload.phoneNumber = payload.phone;
        delete payload.phone;
      }
      if (payload.address1 !== undefined) {
        payload.addressLine1 = payload.address1;
        delete payload.address1;
      }
      if (payload.address2 !== undefined) {
        payload.addressLine2 = payload.address2;
        delete payload.address2;
      }
      if (payload.projectManager === "") {
        payload.projectManager = null; // Send null to unset PM instead of empty string which causes CastError
      }

      if (selectedProject) {
        await projectService.update(selectedProject._id, payload);
        showSuccessMessage("Project updated successfully");
      } else {
        await projectService.create({
          ...payload,
          isMovedToProject: 'project',
          status: 'cust_acceptance',
          projectStatus: 'Not started',
        });
        showSuccessMessage("Project added successfully");
      }
      refetch();
      setIsFormOpen(false);
      setSelectedProject(null);
    } catch (err) {
      showApiError(err);
    }
  };

  const handleUpdateProject = async (id, data) => {
    try {
      const response = await projectService.update(id, data);
      const emailStatus = response.data?.emailStatus || response.emailStatus;

      if (emailStatus === "sent") {
        showSuccessMessage("Project updated & Quotation sent via email!");
      } else if (emailStatus === "invalid_address") {
        showErrorMessage(
          "Project updated but Address not found! Please check the email address.",
        );
      } else if (emailStatus === "skipped") {
        showSuccessMessage(
          "Project updated (Email skipped: invalid address). You can download it manually from the View modal.",
        );
      } else if (emailStatus === "failed") {
        showErrorMessage(
          "Project updated but email delivery failed. Please download manually from the View modal.",
        );
      } else {
        showSuccessMessage("Project updated");
      }
      refetch();
    } catch (err) {
      showApiError(err);
    }
  };

  const toggleProjectStatus = (project, targetStatus) => {
    if (project.isLocked) {
      showErrorMessage("Project status is locked");
      return;
    }
    if (targetStatus === "design") {
      setDesignModalProject(project);
      setIsDesignFormOpen(true);
      return;
    }

    const formStages = [
      "appointment_scheduled",
      "site_visit",
      "inspection_done",
      "quotation_sent",
      "approved",
      "project_started",
      "assigned",
      "drop",
      "design_approval",
      "final_quotation",
      "quotation_approval",
      "documents"
    ];
    if (formStages.includes(targetStatus)) {
      setPendingStatus({ project, targetStatus });
    } else {
      handleUpdateProject(project._id, { status: targetStatus });
    }
  };

  const handleDesignSubmit = async (formDataArray) => {
    try {
      await Promise.all(
        formDataArray.map((fd) => designService.uploadMedia(fd))
      );
      if (designModalProject && designModalProject.status !== 'design') {
        await projectService.update(designModalProject._id, { status: 'design' });
      }
      showSuccessMessage("Design media uploaded & status updated!");
      setIsDesignFormOpen(false);
      setDesignModalProject(null);
      refetch();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const res = await projectService.getProjects({
        ...queryParams,
        limit: 10000,
        page: 1,
      });

      const data = res?.data || res;
      const exportProjects = data.leads || data.projects || (Array.isArray(data) ? data : []);

      const exportData = getProjectExportData(exportProjects);

      exportToExcel(exportData, {
        filename: `Verve_Projects_Export_${new Date().toLocaleDateString().replace(/\//g, "-")}`,
      });
      showSuccessMessage("Export successful!");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({
      status: "all",
      activityStatus: "all",
      source: "all",
      city: "all",
      assignedTo: "all",
      date: "all",
      fromDate: "",
      toDate: "",
    });
  };
  if (projectsLoading && !projects.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader text="Loading projects..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      {/* Brand Hero Banner */}
      {filters.status === 'all' && (
        <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
          <div className="pl-8 z-20">
            <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
              Verve Projects
            </h1>
            <p className="text-sm font-body text-slate-400 mt-2 font-medium">
              Verve Kitchens Projects Management
            </p>
          </div>
          <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
            <img
              src="/kitchens/1.jpg"
              alt="Project Management"
              className="absolute top-0 right-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      )}

      {filters.status === 'all' && user?.role !== "designer" && (
        <ProjectsStats
          stats={stats}
          isExpanded={isStatsExpanded}
          onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
        />
      )}
{/* updated by Halimunnisa - 2026-06-09: Hiding pipeline tabs as per new workflow separation, will be re-introduced inside Projects module with relevant stages.*/}
      {/* <PipelineTabs  
        currentTab={filters.status === 'all' && user?.role === 'designer' ? 'design' : (filters.status === 'all' ? 'all' : filters.status)} 
        onTabChange={(tab) => setFilters('status', tab)}
        stages={user?.role === "designer" ? PROJECT_PIPELINE_STAGES.filter(s => s.key === "design") : PROJECT_PIPELINE_STAGES}
        allLabel="All Projects"
        hideAll={user?.role === "designer"}
      /> */}
      
      {(filters.status === 'all' || user?.role === 'designer' || isProjectManager || isFieldOfficer || isAccountant) && (
        <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
          <ProjectsControlPanel
            search={search} setSearch={setSearch}
            filters={filters} setFilters={setFilters}
            sortBy={sortBy} setSortBy={setSortBy}
            showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
            view={view} setView={setView}
            onAddClick={user?.role === 'designer' ? undefined : () => { setSelectedProject(null); setIsFormOpen(true); }}
            onExport={handleExport}
            isExporting={isExporting}
            activityStats={activityStats}
            filterOptions={filterOptions}
            onReset={handleReset}
          />
        </div>
      )}

      <ProjectsTabContent
        view={view}
        filters={filters}
        projects={projects}
        projectsLoading={projectsLoading}
        user={user}
        teamMembers={team}
        salesExecs={salesExecs}
        projectManagers={projectManagers}
        setSelectedProject={setSelectedProject}
        setIsDetailOpen={setIsDetailOpen}
        setIsFormOpen={setIsFormOpen}
        setConfirmState={setConfirmState}
        refetch={refetch}
        toggleProjectStatus={toggleProjectStatus}
        handleUpdateProject={handleUpdateProject}
        stages={PROJECT_PIPELINE_STAGES}
      />

      {totalProjects > 10 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalProjects / 10)}
          onPageChange={setPage}
        />
      )}

      <AddProject
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedProject(null);
        }}
        onSubmit={handleProjectSubmit}
        initialData={activeProject}
        teamMembers={team}
        user={user}
      />

      <ChangeStatusForm
        isOpen={!!pendingStatus}
        onClose={() => setPendingStatus(null)}
        onConfirm={async (extraData) => {
          if (!pendingStatus?.project?._id) return;
          const response = await projectService.update(
            pendingStatus.project._id,
            { ...extraData, status: pendingStatus.targetStatus },
          );

          const emailStatus =
            response.data?.emailStatus || response.emailStatus;
          if (emailStatus === "sent") {
            showSuccessMessage("Status updated & Quotation sent!");
          } else if (emailStatus === "invalid_address") {
            showErrorMessage(
              "Status updated but Address not found! Please check the email address.",
            );
          } else if (emailStatus === "skipped") {
            showSuccessMessage(
              "Status updated (Email skipped: No valid address). You can download and send it manually from the View modal.",
            );
          } else if (emailStatus === "failed") {
            showErrorMessage(
              "Status updated but email failed to send. Please download manually from the View modal.",
            );
          } else {
            showSuccessMessage("Status updated successfully");
          }

          setPendingStatus(null);
          refetch();
        }}
        pendingStatus={pendingStatus}
        teamMembers={team}
        stages={LEAD_STAGES}
        user={user}
      />

      <ViewProject
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedProject(null);
        }}
        project={activeProject}
        user={user}
        stages={LEAD_STAGES}
        teamMembers={team}
        projectManagers={projectManagers}
        updateStatus={toggleProjectStatus}
        updateProjectState={handleUpdateProject}
        onToggleDisable={async (project) => {
          // Re-use the same confirmation state for consistency
          const isEnabling = project.isDisabled;
          setConfirmState({
            isOpen: true,
            title: isEnabling ? "Enable Project" : "Disable Project",
            message: `Are you sure you want to ${isEnabling ? "enable" : "disable"} this project record?`,
            variant: isEnabling ? "info" : "danger",
            onConfirm: async () => {
              setConfirmState((prev) => ({ ...prev, loading: true }));
              try {
                await projectService.toggleDisable(
                  project._id,
                  !project.isDisabled,
                );
                showSuccessMessage(
                  `Project ${isEnabling ? "enabled" : "disabled"} successfully`,
                );
                refetch();
              } catch (err) {
                showApiError(err);
              } finally {
                setConfirmState({ isOpen: false });
              }
            },
          });
        }}
      />

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        loading={confirmState.loading}
      />

      <AddDesign
        isOpen={isDesignFormOpen}
        onClose={() => {
          setIsDesignFormOpen(false);
          setDesignModalProject(null);
        }}
        onSubmit={handleDesignSubmit}
        initialData={designModalProject}
        teamMembers={team}
        user={user}
      />
    </div>
  );
}
